import { apiCall } from '@/lib/api';
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query';
import type {
  ReleaseEnvironment,
  UpdateReleaseChannelBody,
  UpdateReleaseProductBody,
} from '@uchat-team-management/shared';
import {
  getReleaseIntegrationsStatus,
  getUpdateManifest,
  listFadTesterGroups,
  listReleaseProducts,
  sendSlackTestMessage,
  syncFadTesterGroups,
  updateReleaseChannel,
  updateReleaseProduct,
} from './api';

export const releaseProductsKey = ['release-products'] as const;

/** Disabled products are excluded by default; config surfaces pass true to manage them. */
export const useReleaseProducts = (includeDisabled?: boolean) =>
  createQuery(() => ({
    queryKey: [...releaseProductsKey, { includeDisabled: includeDisabled === true }] as const,
    queryFn: () =>
      apiCall(listReleaseProducts, includeDisabled ? { query: { includeDisabled: 'true' } } : {}),
  }));

/** Lazy read-back of the published latest.json — enable on first expand; solid-query caches after. */
export const useUpdateManifest = (
  input: () => { productId: string; environment: ReleaseEnvironment; enabled: boolean },
) =>
  createQuery(() => ({
    queryKey: ['update-manifest', input().productId, input().environment] as const,
    queryFn: () =>
      apiCall(getUpdateManifest, {
        params: { productId: input().productId },
        query: { environment: input().environment },
      }),
    enabled: input().enabled && !!input().productId,
  }));

/** Tester groups (UCH-513) — served from the channel's synced cache, options-only source. */
export const useFadTesterGroups = (
  input: () => { productId: string; environment: ReleaseEnvironment } | null,
) =>
  createQuery(() => ({
    enabled: input() !== null,
    queryKey: ['fad-groups', input()] as const,
    retry: false,
    queryFn: () => {
      const params = input();
      if (!params) throw new Error('unreachable: query disabled');
      return apiCall(listFadTesterGroups, { params });
    },
  }));

/** Force-refresh the channel's tester-group cache from FAD (product-config Sync button). */
export const useSyncFadGroups = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { productId: string; environment: ReleaseEnvironment }) =>
      apiCall(syncFadTesterGroups, { params: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fad-groups'] }),
  }));
};

export const useReleaseIntegrations = () =>
  createQuery(() => ({
    queryKey: ['release-integrations'] as const,
    queryFn: () => apiCall(getReleaseIntegrationsStatus, {}),
  }));

export const useUpdateReleaseProduct = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { productId: string; body: UpdateReleaseProductBody }) =>
      apiCall(updateReleaseProduct, { params: { productId: input.productId }, body: input.body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: releaseProductsKey }),
  }));
};

export const useUpdateReleaseChannel = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      productId: string;
      environment: ReleaseEnvironment;
      body: UpdateReleaseChannelBody;
    }) =>
      apiCall(updateReleaseChannel, {
        params: { productId: input.productId, environment: input.environment },
        body: input.body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: releaseProductsKey }),
  }));
};

/** Slack notifications card "Send test" — posts to the saved channel id. */
export const useSendSlackTest = () =>
  createMutation(() => ({
    mutationFn: (input: { productId: string; environment: ReleaseEnvironment }) =>
      apiCall(sendSlackTestMessage, { params: input }),
  }));
